import { useEffect, useRef, useState } from "react";

const HOVER_TARGETS = "a, button, input, textarea, select, [role='button'], [data-cursor-hover]";
const DEFAULT_GLOW_CLASSES = [
  "bg-gradient-to-br",
  "from-pink-300/25",
  "via-rose-300/15",
  "to-purple-300/20"
];
const GLOW_COLOR_CLASSES = [
  "from-pink-400/40",
  "to-rose-400/40",
  "from-purple-400/40",
  "to-pink-400/40",
  "from-yellow-300/30",
  "to-pink-400/40",
  "from-blue-400/30",
  "to-purple-400/40",
  "from-pink-400/30",
  "to-purple-400/30",
  "via-rose-300/15",
  "from-pink-300/25",
  "to-purple-300/20"
];
const DOT_COLOR_CLASSES = ["bg-rose-500", "bg-purple-500", "bg-yellow-400", "bg-blue-500", "bg-pink-500"];

export default function CursorGlow() {
  const glowRef = useRef(null);
  const dotRef = useRef(null);
  const trailOneRef = useRef(null);
  const trailTwoRef = useRef(null);
  const pointerPosRef = useRef({ x: 0, y: 0 });
  const hoverRef = useRef(false);
  const movingRef = useRef(false);
  const sparkTimeoutRef = useRef(null);

  const [isPointerDown, setIsPointerDown] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [clickPos, setClickPos] = useState(null);

  const updateHoverState = (value) => {
    if (hoverRef.current === value) return;
    hoverRef.current = value;
    setIsHovering(value);
  };

  const updateMovingState = (value) => {
    if (movingRef.current === value) return;
    movingRef.current = value;
    setIsMoving(value);
  };

  const applyGlowColors = (glow, ...classes) => {
    glow.classList.remove(...GLOW_COLOR_CLASSES);
    glow.classList.add(...classes);
  };

  const resetGlowColors = (glow, dot) => {
    glow.classList.remove(...GLOW_COLOR_CLASSES);
    glow.classList.add(...DEFAULT_GLOW_CLASSES);
    dot.classList.remove(...DOT_COLOR_CLASSES);
    dot.classList.add("bg-pink-500");
  };

  useEffect(() => {
    const glow = glowRef.current;
    const dot = dotRef.current;
    const trailOne = trailOneRef.current;
    const trailTwo = trailTwoRef.current;
    if (!glow || !dot || !trailOne || !trailTwo) {
      return undefined;
    }

    if (window.matchMedia("(pointer: coarse)").matches) {
      [glow, dot, trailOne, trailTwo].forEach((el) => {
        if (el) el.style.display = "none";
      });
      return undefined;
    }

    let glowX = 0;
    let glowY = 0;
    let trailOneX = 0;
    let trailOneY = 0;
    let trailTwoX = 0;
    let trailTwoY = 0;
    let raf;
    let moveTimeout;

    const onMouseMove = (event) => {
      pointerPosRef.current = { x: event.clientX, y: event.clientY };

      if (!movingRef.current) {
        updateMovingState(true);
      }
      clearTimeout(moveTimeout);
      moveTimeout = setTimeout(() => updateMovingState(false), 120);
    };

    const animate = () => {
      const { x: targetX, y: targetY } = pointerPosRef.current;
      const glowLerp = hoverRef.current ? 0.25 : 0.18;
      const trailOneLerp = hoverRef.current ? 0.15 : 0.12;
      const trailTwoLerp = hoverRef.current ? 0.09 : 0.08;

      glowX += (targetX - glowX) * glowLerp;
      glowY += (targetY - glowY) * glowLerp;
      trailOneX += (targetX - trailOneX) * trailOneLerp;
      trailOneY += (targetY - trailOneY) * trailOneLerp;
      trailTwoX += (targetX - trailTwoX) * trailTwoLerp;
      trailTwoY += (targetY - trailTwoY) * trailTwoLerp;

      glow.style.transform = `translate3d(${glowX - 24}px, ${glowY - 24}px, 0)`;
      dot.style.transform = `translate3d(${targetX - 2.5}px, ${targetY - 2.5}px, 0)`;
      trailOne.style.transform = `translate3d(${trailOneX - 14}px, ${trailOneY - 14}px, 0)`;
      trailTwo.style.transform = `translate3d(${trailTwoX - 12}px, ${trailTwoY - 12}px, 0)`;

      if (!movingRef.current && !hoverRef.current) {
        const pulseScale = 1 + Math.sin(Date.now() * 0.003) * 0.05;
        glow.style.transform += ` scale(${pulseScale})`;
      }

      raf = requestAnimationFrame(animate);
    };

    const onMouseOver = (event) => {
      const target = event.target.closest(HOVER_TARGETS);
      if (!target) {
        updateHoverState(false);
        resetGlowColors(glow, dot);
        glow.classList.remove("scale-150", "blur-3xl");
        dot.classList.remove("scale-200", "h-1.5", "w-1.5");
        return;
      }

      updateHoverState(true);

      resetGlowColors(glow, dot);

      if (target.tagName === "A") {
        applyGlowColors(glow, "bg-gradient-to-br", "from-pink-400/40", "to-rose-400/40");
        dot.classList.add("bg-rose-500");
      } else if (target.tagName === "BUTTON") {
        applyGlowColors(glow, "bg-gradient-to-br", "from-purple-400/40", "to-pink-400/40");
        dot.classList.add("bg-purple-500");
      } else if (target.hasAttribute("data-cursor-hover")) {
        const hoverType = target.getAttribute("data-cursor-hover");
        if (hoverType === "cake") {
          applyGlowColors(glow, "bg-gradient-to-br", "from-yellow-300/30", "to-pink-400/40");
          dot.classList.add("bg-yellow-400");
        } else if (hoverType === "project") {
          applyGlowColors(glow, "bg-gradient-to-br", "from-blue-400/30", "to-purple-400/40");
          dot.classList.add("bg-blue-500");
        } else {
          applyGlowColors(glow, "bg-gradient-to-br", "from-pink-400/30", "to-purple-400/30");
          dot.classList.add("bg-pink-500");
        }
      } else {
        applyGlowColors(glow, "bg-gradient-to-br", "from-pink-400/30", "to-purple-400/30");
        dot.classList.add("bg-pink-500");
      }

      glow.classList.add("scale-150", "blur-3xl");
      dot.classList.add("scale-200", "h-1.5", "w-1.5");
    };

    const onMouseOut = (event) => {
      if (event.target.closest(HOVER_TARGETS) && !event.relatedTarget?.closest(HOVER_TARGETS)) {
        updateHoverState(false);
        resetGlowColors(glow, dot);
        glow.classList.remove("scale-150", "blur-3xl");
        dot.classList.remove("scale-200", "h-1.5", "w-1.5");
      }
    };

    const onPointerDown = () => {
      setIsPointerDown(true);
      glow.classList.add("scale-75", "opacity-90");
      dot.classList.add("scale-50", "opacity-70");

      setClickPos(pointerPosRef.current);
      clearTimeout(sparkTimeoutRef.current);
      sparkTimeoutRef.current = setTimeout(() => setClickPos(null), 300);
    };

    const onPointerUp = () => {
      setIsPointerDown(false);
      glow.classList.remove("scale-75", "opacity-90");
      dot.classList.remove("scale-50", "opacity-70");
    };

    resetGlowColors(glow, dot);
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseover", onMouseOver, true);
    window.addEventListener("mouseout", onMouseOut, true);
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("mouseup", onPointerUp);
    window.addEventListener("dragstart", onPointerDown);
    window.addEventListener("dragend", onPointerUp);
    raf = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseover", onMouseOver, true);
      window.removeEventListener("mouseout", onMouseOut, true);
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("mouseup", onPointerUp);
      window.removeEventListener("dragstart", onPointerDown);
      window.removeEventListener("dragend", onPointerUp);
      cancelAnimationFrame(raf);
      clearTimeout(moveTimeout);
      clearTimeout(sparkTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div
        ref={glowRef}
        aria-hidden="true"
        className={`
          pointer-events-none fixed left-0 top-0 z-[9999]
          h-12 w-12 rounded-full
          blur-2xl opacity-80
          transition-all duration-200 ease-out
          mix-blend-multiply
          ${isPointerDown ? '!duration-100' : ''}
          ${isHovering ? '!duration-150' : ''}
        `}
      />

      <div
        ref={trailOneRef}
        aria-hidden="true"
        className={`
          pointer-events-none fixed left-0 top-0 z-[9998]
          h-8 w-8 rounded-full
          bg-gradient-to-br from-pink-400/20 to-purple-400/10
          blur-xl opacity-60
          transition-opacity duration-300
          ${!isMoving ? 'opacity-30' : ''}
        `}
      />

      <div
        ref={trailTwoRef}
        aria-hidden="true"
        className={`
          pointer-events-none fixed left-0 top-0 z-[9997]
          h-6 w-6 rounded-full
          bg-gradient-to-br from-rose-400/15 to-pink-400/10
          blur-lg opacity-40
          transition-opacity duration-400
          ${!isMoving ? 'opacity-20' : ''}
        `}
      />

      <div
        ref={dotRef}
        aria-hidden="true"
        className={`
          pointer-events-none fixed left-0 top-0 z-[10000]
          h-2.5 w-2.5 rounded-full
          bg-pink-500
          transition-all duration-150 ease-out
          shadow-[0_0_8px_2px_rgba(236,72,153,0.4)]
          ${isPointerDown ? '!duration-75' : ''}
          ${isHovering ? '!duration-100' : ''}
        `}
      />

      {clickPos && (
        <div
          className="pointer-events-none fixed z-[10001] h-4 w-4 rounded-full bg-gradient-to-br from-yellow-300/60 to-pink-400/60 blur-sm animate-ping"
          style={{
            left: `${clickPos.x}px`,
            top: `${clickPos.y}px`,
            transform: 'translate(-50%, -50%)'
          }}
        />
      )}
    </>
  );
}
